/**
 * @fileoverview User authentication and profile type definitions
 * @active Used throughout the auth service and components
 *
 * This file provides centralized type definitions for authentication-related data
 * structures and user profiles, ensuring DRY principles across the codebase.
 *
 * @see {@link /src/services/auth.service.ts} - Main auth service that uses these types
 * @see {@link /src/app/utils/supabase/client.ts} - Supabase client utilities
 * @see {@link /src/components/auth/SignIn.tsx} - Sign-in component
 * @see {@link /src/components/auth/SignUp.tsx} - Sign-up component
 * @see {@link /src/components/settings/account/profile/connectedAccounts/ConnectedItem.tsx} - Social connections
 * @see {@link /src/services/profile.service.ts} - Profile service that uses these types
 * @see {@link /src/components/settings/account/twoFactor/index.tsx} - Two-factor authentication setup
 */

import type {
  User,
  Session,
  Provider,
  AuthResponse,
  OAuthResponse,
} from "@supabase/supabase-js";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import type { ReactNode } from "react";
import type { FlexProps } from "@chakra-ui/react";
import { z } from "zod";

/**
 * Available social login providers
 * @see {@link https://supabase.com/docs/guides/auth/social-login}
 */
export type SocialProvider = "google" | "linkedin";

/**
 * Centralized configuration for OAuth providers
 */
export const authProviders = {
  google: "google" as Provider,
  linkedin: "linkedin_oidc" as Provider,
} as const;

export type AuthProvider = (typeof authProviders)[keyof typeof authProviders];

/**
 * List of all supported OAuth providers for consistent usage
 */
export const supportedProviders: Provider[] = [
  authProviders.google,
  authProviders.linkedin,
];

/**
 * Custom error object for consistent error handling
 */
export interface CUSTOM_ERRORObject {
  code?: string;
  message: string;
  status: number;
  data?: unknown;
}

/**
 * Type alias for RTK Query return value with proper typing
 */
export type CustomQueryReturnValue<T = unknown> = QueryReturnValue<
  T,
  FetchBaseQueryError
>;

/**
 * TODO: move these to the src/types/error.ts file
 * Maps Supabase Auth error codes to user-friendly messages
 * Centralized error message mapping to maintain consistency
 */
export const authErrorMessages: Record<string, string> = {
  invalid_credentials: "Invalid email or password",
  user_not_found: "No account found with this email",
  invalid_login_credentials: "Invalid email or password",
  email_not_confirmed: "Please verify your email before logging in",
  password_recovery: "Password recovery has been initiated for this account",
  email_taken: "An account with this email already exists",
  weak_password: "Password is too weak. Please use a stronger password",
  invalid_token: "Invalid or expired verification link",
  session_expired: "Your session has expired. Please log in again",
} as const;

/**
 * Gets a user-friendly error message from an error code
 * @param {CUSTOM_ERRORObject} error - The error object from Supabase
 * @returns {string} User-friendly error message
 */
export function getAuthErrorMessage(error: CUSTOM_ERRORObject): string {
  return (
    authErrorMessages[error.code || ""] || error.message || "An error occurred"
  );
}

/**
 * Helper function to create a properly formatted error object
 */
export function createErrorObject(
  message: string,
  code?: string,
  status = 400,
  data?: unknown
): CUSTOM_ERRORObject {
  return { message, code, status, data };
}

/**
 * Handles RTK Query errors consistently
 * @param error - Error from RTK Query
 * @returns Formatted error response
 */
export function handleRTKError(error: unknown): CUSTOM_ERRORObject {
  if (typeof error === "string") {
    return createErrorObject(error);
  }

  if (error && typeof error === "object" && "message" in error) {
    return createErrorObject(
      String(error.message),
      "code" in error ? String(error.code) : undefined,
      "status" in error ? Number(error.status) || 400 : 400,
      error
    );
  }

  return createErrorObject("An unexpected error occurred");
}

// --- Zod Schema for AuthSuccessResponse ---
// Note: Supabase User/Session types are complex; using z.unknown() for now.
// Consider defining minimal placeholder schemas if specific fields are needed.
export const AuthSuccessResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  user: z.unknown().nullable().optional(), // Placeholder for Supabase User
  session: z.unknown().nullable().optional(), // Placeholder for Supabase Session
  provider: z.enum([authProviders.google, authProviders.linkedin]).optional(),
  redirected: z.boolean().optional(),
  url: z.string().url().nullable().optional(),
  userExists: z.boolean().optional(),
  needsSync: z.boolean().optional(),
  userHandled: z.boolean().optional(),
});

// --- Zod-Derived Type (Replaces original interface eventually) ---
export type AuthSuccessResponse = z.infer<typeof AuthSuccessResponseSchema>;

/**
 * Auth success response type (Original Interface - Commented Out for Phase 2)
 * The common response format used by all auth service methods
 */
// export interface AuthSuccessResponse {
//   /** Whether the operation was successful */
//   success: boolean;
//   /** Optional message describing the result */
//   message?: string;
//   /** User object from Supabase Auth */
//   user?: User | null;
//   /** Session object from Supabase Auth */
//   session?: Session | null;
//   /** OAuth provider used for authentication */
//   provider?: AuthProvider;
//   /** Whether the operation resulted in a redirect */
//   redirected?: boolean;
//   /** Redirect URL for OAuth operations */
//   url?: string | null;
//   /** Whether the user already exists (for signup flows) */
//   userExists?: boolean;
//   /** Flag indicating API handled a profile creation or sync issue */
//   needsSync?: boolean;
//   /** Flag indicating user has been handled specially (e.g., magic link sent) */
//   userHandled?: boolean;
// }

// --- Base Types ---

/**
 * Base interface for components that have close functionality
 */
export interface BaseCallbackProps {
  /** Callback when the component is closed */
  onClose?: () => void;
}

/**
 * Base interface for modal components
 */
export interface BaseModalProps extends BaseCallbackProps {
  /** Whether the modal is open */
  isOpen: boolean;
}

/**
 * Base interface for email-only form data and parameters
 */
export interface EmailFormData {
  email: string;
}

/**
 * Base interface for password fields in forms
 */
export interface PasswordData {
  password: string;
}

/**
 * Interface for confirm password forms
 */
export interface PasswordConfirmationData extends PasswordData {
  confirmPassword: string;
}

/**
 * Universal auth credentials combining email and password
 * Used as a base for multiple auth operations
 */
export interface AuthCredentials extends EmailFormData, PasswordData {
  [key: string]: any; // Additional properties for flexibility
}

/**
 * Parameters for sign-in operations
 * Used by both auth service and sign-in form
 * @see {@link https://supabase.com/docs/reference/javascript/auth-signinwithpassword}
 * @see {@link src/components/auth/SignIn.tsx} - Sign-in form
 */
// --- Zod Schema for SignInParams ---
export const SignInParamsSchema = z
  .object({
    email: z.string().email(),
    password: z.string(),
    onClose: z.function().optional(), // Representing () => void
    // Allow additional properties from AuthCredentials if needed via .passthrough()
  })
  .passthrough();

// --- Zod-Derived Type (Replaces original interface eventually) ---
export type SignInParams = z.infer<typeof SignInParamsSchema>;

/**
 * Parameters for sign-in operations (Original Interface - Commented Out for Phase 2)
 * Used by both auth service and sign-in form
 * @see {@link https://supabase.com/docs/reference/javascript/auth-signinwithpassword}
 * @see {@link src/components/auth/SignIn.tsx} - Sign-in form
 */
// export interface SignInParams extends AuthCredentials, BaseCallbackProps {}

/**
 * Sign-up parameters
 * Extends credentials with name fields and metadata
 */
// --- Zod Schema for SignUpParams ---
export const SignUpParamsSchema = z
  .object({
    email: z.string().email(),
    password: z.string(),
    confirmPassword: z.string(), // Validation logic (matching password) is typically done outside the schema
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    onClose: z.function().optional(), // Representing () => void
    metadata: z
      .object({
        first_name: z.string().optional(),
        last_name: z.string().optional(),
      })
      .passthrough()
      .optional(), // Allow other metadata keys
    // Allow additional properties from AuthCredentials if needed via .passthrough()
  })
  .passthrough();

// --- Zod-Derived Type (Replaces original interface eventually) ---
export type SignUpParams = z.infer<typeof SignUpParamsSchema>;

/**
 * Sign-up parameters (Original Interface - Commented Out for Phase 2)
 * Extends credentials with name fields and metadata
 */
// export interface SignUpParams extends AuthCredentials, BaseCallbackProps {
//   confirmPassword: string; // Required for form validation
//   firstName: string; // Made required to match schema validation
//   lastName: string; // Made required to match schema validation
//   metadata?: {
//     first_name?: string;
//     last_name?: string;
//     [key: string]: any;
//   };
// }

/**
 * Form data interface for sign-in validation
 * Reuses AuthCredentials for consistency
 */
export type SignInFormFields = Omit<AuthCredentials, keyof BaseCallbackProps>;

/**
 * Form data interface for sign-up validation
 * Reuses SignUpParams for consistency, excluding callback props and metadata
 */
export type SignUpFormData = Omit<
  SignUpParams,
  keyof BaseCallbackProps | "metadata"
>;

/**
 * Parameters and options for various auth operations
 */
export namespace AuthOperations {
  /**
   * OAuth parameters
   */
  // --- Zod Schema for AuthOperations.OAuthParams ---
  export const OAuthParamsSchema = z.object({
    email: z.string().email().optional(),
    access_token: z.string().optional(),
    authCode: z.string().optional(),
    is_connect: z.boolean().optional(),
    skipBrowserRedirect: z.boolean().optional(),
    onClose: z.function().optional(), // Representing () => void
  });

  // --- Zod-Derived Type (Replaces original interface eventually) ---
  export type OAuthParams = z.infer<typeof OAuthParamsSchema>;

  /**
   * OAuth parameters (Original Interface - Commented Out for Phase 2)
   */
  // export interface OAuthParams extends BaseCallbackProps {
  //   email?: string; // Optional for OAuth flows
  //   access_token?: string;
  //   authCode?: string;
  //   is_connect?: boolean;
  //   skipBrowserRedirect?: boolean;
  // }

  /**
   * Parameters for all password operations (update, reset, add)
   */
  // --- Zod Schema for AuthOperations.PasswordParams ---
  export const PasswordParamsSchema = z.object({
    password: z.string(),
    token: z.string().optional(),
    email: z.string().email().nullable().optional(),
    accessToken: z.string().nullable().optional(),
    isForgot: z.boolean().optional(),
  });

  // --- Zod-Derived Type (Replaces original interface eventually) ---
  export type PasswordParams = z.infer<typeof PasswordParamsSchema>;

  /**
   * Parameters for all password operations (update, reset, add) (Original Interface - Commented Out for Phase 2)
   */
  // export interface PasswordParams extends PasswordData {
  //   token?: string; // For password reset
  //   email?: string | null; // For password reset email target
  //   accessToken?: string | null; // Potentially for session context during update
  //   isForgot?: boolean; // Flag for forgot password flow context
  // }

  /**
   * Parameters for email verification
   */
  // --- Zod Schema for AuthOperations.VerifyEmailParams ---
  export const VerifyEmailParamsSchema = z.object({
    email: z.string().email(),
    token: z.string(),
    type: z.enum(["email", "recovery", "invite"]),
  });

  // --- Zod-Derived Type (Replaces original interface eventually) ---
  export type VerifyEmailParams = z.infer<typeof VerifyEmailParamsSchema>;

  /**
   * Parameters for email verification (Original Interface - Commented Out for Phase 2)
   */
  // export interface VerifyEmailParams extends EmailFormData {
  //   token: string;
  //   type: "email" | "recovery" | "invite";
  // }

  /**
   * Options for sign-out operations
   */
  export interface SignOutOptions extends BaseCallbackProps {
    redirectTo?: string;
  }

  /**
   * Form data interface for two-factor authentication code input
   */
  export interface TwoFCodeFormData {
    code2Fa: string;
  }
}

/**
 * Namespace for Multi-Factor Authentication (MFA) related types
 * Centralized MFA-specific interfaces for the Supabase Auth MFA flow
 * @see {@link https://supabase.com/docs/reference/javascript/auth-mfa-enroll}
 */
export namespace MFA {
  /**
   * Response from MFA enrollment (QR code generation)
   * Contains the QR code as SVG and the secret for manual entry
   */
  // --- Zod Schema for MFA.EnrollmentResponse ---
  export const EnrollmentResponseSchema = z.object({
    qr_code: z.string(), // Assuming SVG is a string
    secret: z.string(),
    factorId: z.string(),
  });

  // --- Zod-Derived Type (Replaces original interface eventually) ---
  export type EnrollmentResponse = z.infer<typeof EnrollmentResponseSchema>;

  /**
   * Response from MFA enrollment (QR code generation) (Original Interface - Commented Out for Phase 2)
   * Contains the QR code as SVG and the secret for manual entry
   */
  // export interface EnrollmentResponse {
  //   /** QR code as an SVG string */
  //   qr_code: string;
  //   /** Secret key for manual entry */
  //   secret: string;
  //   /** Factor ID needed for challenge/verify */
  //   factorId: string;
  // }

  /**
   * Response from MFA verification
   */
  // --- Zod Schema for MFA.VerificationResponse ---
  export const VerificationResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
  });

  // --- Zod-Derived Type (Replaces original interface eventually) ---
  export type VerificationResponse = z.infer<typeof VerificationResponseSchema>;

  /**
   * Response from MFA verification (Original Interface - Commented Out for Phase 2)
   */
  // export interface VerificationResponse {
  //   /** Whether verification was successful */
  //   success: boolean;
  //   /** Status message */
  //   message: string;
  // }

  /**
   * Parameters for challenging and verifying an MFA factor
   */
  // --- Zod Schema for MFA.ChallengeVerifyParams ---
  export const ChallengeVerifyParamsSchema = z.object({
    factorId: z.string(),
    code: z.string(), // Typically a 6-digit code
  });

  // --- Zod-Derived Type (Replaces original interface eventually) ---
  export type ChallengeVerifyParams = z.infer<
    typeof ChallengeVerifyParamsSchema
  >;

  /**
   * Parameters for challenging and verifying an MFA factor (Original Interface - Commented Out for Phase 2)
   */
  // export interface ChallengeVerifyParams {
  //   /** Factor ID from enrollment */
  //   factorId: string;
  //   /** Verification code from authenticator app */
  //   code: string;
  // }
}

// Re-export for backward compatibility (Keep these for now)
export type OAuthParams = AuthOperations.OAuthParams;
export type PasswordParams = AuthOperations.PasswordParams;
export type VerifyEmailParams = AuthOperations.VerifyEmailParams;
export type SignOutOptions = AuthOperations.SignOutOptions;
export type TwoFCodeFormData = AuthOperations.TwoFCodeFormData;

// --- Zod Schema for SocialLinks ---
export const SocialLinksSchema = z.object({
  linkedin: z.string().url("Invalid LinkedIn URL").nullable().optional(),
  twitter: z.string().url("Invalid Twitter URL").nullable().optional(),
  website: z.string().url("Invalid Website URL").nullable().optional(),
});

// --- Zod-Derived Type (Replaces original interface) ---
export type SocialLinks = z.infer<typeof SocialLinksSchema>;

// --- User Profile & Update Types ---

// --- Zod Schema for UserProfile ---
export const UserProfileSchema = z.object({
  id: z.string().uuid("Invalid User ID format"),
  email: z.string().email("Invalid email address"),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  avatar_url: z.string().url("Invalid Avatar URL").nullable().optional(),
  user_photo_upload: z.string().nullable().optional(), // Path, not URL
  created_at: z.string().datetime({ message: "Invalid creation timestamp" }),
  updated_at: z.string().datetime({ message: "Invalid update timestamp" }),
  last_sign_in: z
    .string()
    .datetime({ message: "Invalid sign-in timestamp" })
    .nullable()
    .optional(),
  is_verified: z.boolean(),
  is_active: z.boolean(),
  roles: z.array(z.string()).nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(), // Supabase metadata is JSONB
  subscriptionPlan: z.string().nullable().optional(),
  userSocialLoginGoogleUid: z.string().nullable().optional(),
  userSocialLoginLinkedinUid: z.string().nullable().optional(),
  userSocialLoginAppleUid: z.string().nullable().optional(),
  userSocialLoginTwitterUid: z.string().nullable().optional(),
  user_linkedin_id: z.string().nullable().optional(), // Keep as string, validation in normalizeSocialLinksForDB
  user_twitter_id: z.string().nullable().optional(), // Keep as string
  user_website_id: z.string().nullable().optional(), // Keep as string
  auth2fa: z.boolean().nullable().optional(),
});

// --- Zod-Derived Type (Replaces original interface) ---
export type UserProfile = z.infer<typeof UserProfileSchema>;

/**
 * Response from billing API calls
 * Handles multiple possible response formats from billing services
 */
// --- Zod Schema for BillingResponse ---
export const BillingResponseSchema = z
  .object({
    data: z
      .object({
        message: z.string().optional(),
      })
      .passthrough()
      .optional(), // Allow other keys in data
    message: z.string().optional(),
    url: z.string().url().optional(),
  })
  .passthrough(); // Allow other top-level keys

// --- Zod-Derived Type (Replaces original interface eventually) ---
export type BillingResponse = z.infer<typeof BillingResponseSchema>;

/**
 * Response from billing API calls (Original Interface - Commented Out for Phase 2)
 * Handles multiple possible response formats from billing services
 */
// export interface BillingResponse {
//   data?: {
//     message?: string;
//     [key: string]: any;
//   };
//   message?: string;
//   url?: string;
//   [key: string]: any;
// }

/**
 * Generic billing API response that could be an array of responses
 * Used by components that consume the getUserBilling endpoint
 */
export type BillingApiResponse = BillingResponse | BillingResponse[] | null;

/**
 * Type guard to check if a value is a user profile
 */
export function isUserProfile(value: any): value is UserProfile {
  // Use Zod's safeParse for runtime validation
  return UserProfileSchema.safeParse(value).success;
}

// --- Zod Schema for BaseUserUpdateOptions ---
export const BaseUserUpdateOptionsSchema = z.object({
  showSuccessMessage: z.boolean().optional(),
  isBlurPopup: z.boolean().optional(),
  successMessage: z.string().optional(),
});

// --- Zod-Derived Type (Replaces original interface) ---
export type BaseUserUpdateOptions = z.infer<typeof BaseUserUpdateOptionsSchema>;

// --- Zod Schema for UserProfileUpdate ---
// Extends BaseUserUpdateOptionsSchema and adds profile-specific fields
export const UserProfileUpdateSchema = BaseUserUpdateOptionsSchema.extend({
  first_name: z
    .string()
    .min(1, "First name cannot be empty")
    .nullable()
    .optional(), // Add min length validation if needed
  last_name: z
    .string()
    .min(1, "Last name cannot be empty")
    .nullable()
    .optional(), // Add min length validation if needed
  user_linkedin_id: z.string().nullable().optional(), // Keep as string, validation elsewhere
  user_twitter_id: z.string().nullable().optional(), // Keep as string
  user_website_id: z.string().nullable().optional(), // Keep as string
  user_photo_upload: z.string().nullable().optional(), // Path in storage
}).passthrough(); // Use passthrough to allow other BaseUserUpdateOptions fields

// --- Zod-Derived Type (Replaces original interface) ---
export type UserProfileUpdate = z.infer<typeof UserProfileUpdateSchema>;

/**
 * Derives display name from user profile
 */
export function getDisplayName(profile: UserProfile | null): string {
  if (!profile) return "User";
  // Access Zod-derived type properties
  if (profile.first_name && profile.last_name) {
    return `${profile.first_name} ${profile.last_name}`;
  }
  if (profile.first_name) return profile.first_name;
  if (profile.last_name) return profile.last_name;
  return profile.email?.split("@")[0] || "User";
}

/**
 * Utility function to normalize social links from UI (camelCase) to DB (snake_case) format.
 */
export function normalizeSocialLinksForDB(
  links: Partial<SocialLinks> // Input uses Zod-derived SocialLinks
): Pick<
  UserProfileUpdate, // Output uses Zod-derived UserProfileUpdate
  "user_linkedin_id" | "user_twitter_id" | "user_website_id"
> {
  let formattedLinkedin = links.linkedin ?? null;
  // Basic check, more robust URL validation happens via Zod if needed elsewhere
  if (
    formattedLinkedin &&
    !formattedLinkedin.startsWith("https://www.linkedin.com/")
  ) {
    // Attempt to format common variations, but primary validation should be at input or via Zod schema
    formattedLinkedin = `https://www.linkedin.com/in/${formattedLinkedin.replace(/^https?:\/\/(www\.)?linkedin\.com\/(in\/)?/, "")}`;
  }
  return {
    user_linkedin_id: formattedLinkedin,
    user_twitter_id: links.twitter ?? null,
    user_website_id: links.website ?? null,
  };
}

/**
 * Type guard to check if a value is a SocialLinks object
 */
export function isSocialLinks(value: any): value is SocialLinks {
  // Use Zod's safeParse for runtime validation
  return SocialLinksSchema.safeParse(value).success;
}

// --- Component & UI Types ---

/**
 * Namespace for UI component prop types
 * Organizes various component-related interfaces in one place
 */
export namespace UI {
  /**
   * Base props interface for all settings components
   */
  export interface BaseSettingsProps extends BaseCallbackProps {
    /** Optional styling overrides */
    styles?: Record<string, unknown>;
  }

  /**
   * Interface for components requiring investor status information
   */
  export interface WithInvestorProps {
    /** Whether the user is an investor, determines which UI elements to show */
    isInvestor: boolean;
  }

  /**
   * Interface for components requiring user profile data
   */
  export interface WithProfileProps {
    /** User profile data (now using Zod-derived type) */
    profile: UserProfile | null;
  }

  /**
   * Interface for funding methods component
   */
  export interface FundingMethodsProps extends BaseSettingsProps {
    /** Whether this is being rendered on the withdrawal page */
    isWithdrawalPage: boolean;
  }

  /**
   * Interface for change password component
   */
  export interface ChangePasswordProps extends BaseSettingsProps {
    /** User's email address for the password change operation */
    email: string;
  }

  /**
   * Interface for input configuration used in form components
   */
  export interface InputConfig {
    id: string;
    type?: string;
    placeholder?: string;
    h?: string | number;
    isDisabled?: boolean;
    [key: string]: any;
  }

  /**
   * Connected social account item properties
   */
  export interface ConnectedItemProps {
    item: {
      id: number;
      title: string;
      provider: SocialProvider;
      icon: ReactNode;
      connected: boolean | string | null | undefined;
    };
  }

  /**
   * Props for the Step3 component in the two-factor authentication flow
   */
  export interface Step3Props {
    /** Callback to set 2FA success state */
    setIsSuccess2FA: (value: boolean) => void;
    /** Factor ID from enrollment */
    factorId: string;
  }

  /**
   * Props for the TwoFactorCard component in the two-factor authentication flow
   */
  export interface TwoFactorCardProps {
    /** Card content */
    children: ReactNode;
    /** Step indicator (e.g., "Step 1") */
    step: string;
    /** Heading describing the step */
    stepHeading: string;
    /** Detailed description of the step */
    stepDescription: string;
    /** Optional custom styling for the card container */
    cardStyle?: FlexProps;
  }

  /**
   * Props for the TwoFSuccessMessage component
   * Shows success message after 2FA setup and redirects the user
   */
  export interface TwoFSuccessMessageProps {
    /** Optional redirect path override */
    redirectPath?: string;
    /** Optional redirect delay in seconds (default: 5) */
    redirectDelay?: number;
  }

  /**
   * Props interface for two-factor authentication component
   */
  export interface TwoFCodeProps extends BaseCallbackProps {
    provider: SocialProvider | "credentials";
    access_token?: string;
    userData?: AuthCredentials; // Keep original type for now, consider Zod later if needed
  }

  /**
   * Props for the TwoFAModal component
   */
  export interface TwoFAModalProps extends BaseModalProps {
    access_token?: string;
    provider: SocialProvider | "credentials";
    userData?: AuthCredentials; // Keep original type for now
  }

  /**
   * Form data interface for password change
   * @see {@link src/validations/formValidation.ts} - changePasswordSchema validation
   * @see {@link src/components/settings/account/changePassword/index.tsx}
   */
  export type ChangePasswordFormData = {
    password: string;
    newPassword: string;
    confirmNewPassword?: string;
  };

  /**
   * Props for the ChangePassword component
   * @see {@link src/services/profile.service.ts} - changePassword mutation requires email
   * @see {@link src/components/settings/account/changePassword/index.tsx}
   */
  export interface ChangePasswordProps extends BaseSettingsProps {
    /** User email address */
    email: string;
  }
}

// Aliases for commonly used component props (for backward compatibility)
export type BaseSettingsProps = UI.BaseSettingsProps;
export type WithProfileProps = UI.WithProfileProps;
export type ConnectedItemProps = UI.ConnectedItemProps;
export type TwoFCodeProps = UI.TwoFCodeProps;
export type TwoFAModalProps = UI.TwoFAModalProps;
export type InputConfig = UI.InputConfig;
export type FundingMethodsProps = UI.FundingMethodsProps;
export type ChangePasswordProps = UI.ChangePasswordProps; // Keep this alias
export type ChangePasswordFormData = UI.ChangePasswordFormData; // Add alias for form data
export type Step3Props = UI.Step3Props;
export type TwoFactorCardProps = UI.TwoFactorCardProps;
export type TwoFSuccessMessageProps = UI.TwoFSuccessMessageProps;

// Removed duplicate definitions that were outside the UI namespace

// Settings component types
export interface SettingsComponentProps
  extends BaseSettingsProps,
    UI.WithInvestorProps {}
export interface NotificationSettingsProps extends SettingsComponentProps {}
export interface ResearchSubscriptionProps extends SettingsComponentProps {}
export interface DisclosuresProps extends SettingsComponentProps {}
export interface ProfileEditComponentProps
  extends WithProfileProps, // Uses Zod-derived UserProfile via WithProfileProps
    BaseSettingsProps {}

// --- Redux State ---

/**
 * Auth state interface for Redux store
 */
export interface AuthState {
  userIsVerify: string | null;
  userSignUpInfo: Record<string, unknown> | null;
  userSignUpEmail: string;
  // Removed deprecated 2FA fields
  lastError: CUSTOM_ERRORObject | null;
}

/**
 * Interface for the profile slice state in Redux store
 * @interface ProfileState
 */
export interface ProfileState {
  profile: UserProfile | null; // Uses Zod-derived UserProfile
  isConnected: boolean;
  accreditationQuestions: any[]; // TODO: Replace 'any' with a specific type if available
  notificationsSettings: any[]; // TODO: Replace 'any' with a specific type if available
  userNotificationsSettings: any[]; // TODO: Replace 'any' with a specific type if available
  accreditationForm: Record<string, any> | null; // TODO: Replace 'any' with a specific type if available
  countPage: number | null;
}
